/**
 * 可见渠道账号请求合并 - Pinia store
 *
 * /chat-records/accounts 返回当前坐席可见的全部账号，聊天记录、推送、同步记录、各看板、
 * 工作台筛选等多处都要用。这里不缓存结果，每次调用都以接口实时数据为准，
 * 只把同一时刻、同参数的并发调用合并成一次请求，请求结束即释放。
 * key 带上当前 token，换账号前发出的在途请求不会被新账号的调用复用；
 * 授权页增删账号、切换登录账号时 invalidate，丢弃变化前已发出的在途请求。
 * 只决定是否合并请求，不改变请求参数。
 */
import { defineStore } from 'pinia';
/** 直接引具体模块而非 @/api 汇总入口：渠道授权接口会回调本 store 失效在途请求，避免循环引用 */
import { chatRecordsApi } from '@/api/chatRecords';

/** 参数序列化为 key：按键名排序，忽略 undefined，保证同参数不同书写顺序合并到同一个请求 */
const requestKeyOf = (params?: Record<string, unknown>) =>
  JSON.stringify(
    Object.keys(params ?? {})
      .sort()
      .filter((k) => params?.[k] !== undefined)
      .map((k) => [k, params?.[k]]),
  );

export const useChatAccountsStore = defineStore('chatAccounts', () => {
  const inflight = new Map<string, Promise<unknown[]>>();

  /**
   * 拉取账号列表：同参数的并发调用只发一次请求。
   * 合并的调用方共用同一份响应，每个调用方拿到各自的数组与对象副本，互相修改不影响。
   */
  const fetchAccounts = async <T = unknown>(params?: Record<string, unknown>): Promise<T[]> => {
    const key = `${localStorage.getItem('token') ?? ''}|${requestKeyOf(params)}`;
    let pending = inflight.get(key);
    if (!pending) {
      pending = chatRecordsApi.accounts(params).finally(() => {
        if (inflight.get(key) === pending) inflight.delete(key);
      });
      inflight.set(key, pending);
    }
    const list = await pending;
    return list.map((item) =>
      item && typeof item === 'object' ? { ...(item as Record<string, unknown>) } : item,
    ) as T[];
  };

  /** 账号增删、授权变化后调用；切换登录账号时同样调用，之后的调用不再复用变化前发出的请求 */
  const invalidate = () => {
    inflight.clear();
  };

  return { fetchAccounts, invalidate };
});
